import React from "react"
import type { SnapshotSummaryData } from "~/api/snapshot_summaries"
import KpiStrip from "./KpiStrip"
import SectorDonut from "./charts/SectorDonut"
import SegmentDonut from "./charts/SegmentDonut"
import PnlBySeriesBar from "./charts/PnlBySeriesBar"
import CallsDistributionsBar from "./charts/CallsDistributionsBar"
import GeographicDonut from "./charts/GeographicDonut"
import TopExposureTable from "./TopExposureTable"
import AiSummaryBox from "./AiSummaryBox"

interface PortfolioSummaryProps {
  year: number
  quarter: number
  data: SnapshotSummaryData
  contextScoped: boolean
}

export default function PortfolioSummary({ data }: PortfolioSummaryProps) {
  return (
    <div className="b-portfolio-summary">
      {/* KPI Strip */}
      {data.kpi_summary ? (
        <KpiStrip kpi={data.kpi_summary} totals={data.totals} />
      ) : (
        <div className="b-kpi-strip">
          {[
            "Total NAV",
            "Net IRR (ITD)",
            "Blended MOIC",
            "Realized P&L (Q)",
            "Unrealized P&L (Q)",
          ].map((label) => (
            <div key={label} className="b-kpi-card">
              <div className="b-kpi-label">{label}</div>
              <div className="b-kpi-value">—</div>
              <div className="b-kpi-delta b-kpi-delta--flat">—</div>
            </div>
          ))}
        </div>
      )}

      {/* AI Summary */}
      {data.ai_summary && <AiSummaryBox data={data.ai_summary} />}

      {/* Sector + Segment Donuts */}
      <div className="b-ps-row b-ps-row--two">
        <div className="b-ps-card">
          <div className="b-ps-card__header">
            <h2>MV exposure by GICS sector</h2>
            <span className="b-ps-card__sub">As of {data.period.label}</span>
          </div>
          <div className="b-ps-card__body">
            <SectorDonut sectors={data.by_sector} />
          </div>
        </div>
        <div className="b-ps-card">
          <div className="b-ps-card__header">
            <h2>MV by segment</h2>
            <span className="b-ps-card__sub">As of {data.period.label}</span>
          </div>
          <div className="b-ps-card__body">
            <SegmentDonut
              byVintage={data.by_vintage}
              byAssetClass={data.by_asset_class}
              series={data.series}
            />
          </div>
        </div>
      </div>

      {/* P&L by Series */}
      <div className="b-ps-row">
        <div className="b-ps-card">
          <div className="b-ps-card__header">
            <h2>P&amp;L by series</h2>
          </div>
          <div className="b-ps-card__body">
            <PnlBySeriesBar series={data.series} />
          </div>
        </div>
      </div>

      {/* Geographic + Calls/Distributions */}
      <div className="b-ps-row b-ps-row--two">
        <div className="b-ps-card">
          <div className="b-ps-card__header">
            <h2>Geographic exposure</h2>
            <span className="b-ps-card__sub">
              By fair value · US sub-regions
            </span>
          </div>
          <div className="b-ps-card__body">
            <GeographicDonut exposure={data.geographic_exposure} />
          </div>
        </div>
        <div className="b-ps-card">
          <div className="b-ps-card__header">
            <h2>Calls &amp; distributions</h2>
            <span className="b-ps-card__sub">Quarter activity</span>
          </div>
          <div className="b-ps-card__body">
            <CallsDistributionsBar series={data.series} />
          </div>
        </div>
      </div>

      {/* Top Holdings */}
      <div className="b-ps-row">
        <TopExposureTable data={data} />
      </div>
    </div>
  )
}
